import { type HTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import './ExerciseCard.css';

export interface ExerciseCardProps extends HTMLAttributes<HTMLElement> {
  /** Small uppercase label above the prompt (e.g. "TRANSLATE"). */
  kicker?: ReactNode;
  /** Main question / prompt text. */
  prompt?: ReactNode;
  /** BCP-47 tag for the prompt, when it is not the document's language —
   *  exercise cards routinely mix a Georgian prompt with English answers,
   *  and screen readers / TTS need the switch marked. */
  promptLang?: string;
  /** Optional subtitle shown under the prompt. */
  subprompt?: ReactNode;
  /** The interactive area (inputs, chips, choices). */
  children?: ReactNode;
  /** Tighten padding for embedded or stacked layouts. */
  compact?: boolean;
  ref?: Ref<HTMLElement>;
}

/**
 * ExerciseCard — the white framed container that every exercise type
 * sits inside. Prompt at the top, answer area below.
 */
export function ExerciseCard({
  kicker,
  prompt,
  promptLang,
  subprompt,
  children,
  compact,
  className,
  ref,
  ...rest
}: ExerciseCardProps) {
  return (
    <section
      ref={ref}
      className={clsx('fluent-excard', className)}
      data-compact={compact || undefined}
      {...rest}
    >
      {kicker != null && <p className="fluent-excard__kicker">{kicker}</p>}
      {prompt != null && (
        <h2 className="fluent-excard__prompt" lang={promptLang}>
          {prompt}
        </h2>
      )}
      {subprompt != null && <p className="fluent-excard__subprompt">{subprompt}</p>}
      {children != null && <div className="fluent-excard__body">{children}</div>}
    </section>
  );
}
